import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Select,
  VStack,
  Box,
  Text,
  Icon,
  Button,
  useColorModeValue,
  Progress,
  HStack,
  Checkbox,
} from '@chakra-ui/react';
import { FaCloudUploadAlt, FaFile, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';

/**
 * فرم آپلود فایل
 * @param {Object} props - props کامپوننت
 * @param {Function} props.onSubmit - تابع ارسال فرم
 * @param {boolean} props.isLoading - وضعیت بارگذاری
 */
const FileUploadForm = forwardRef(({ onSubmit, isLoading }, ref) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isPublic, setIsPublic] = useState(false);
  const fileInputRef = useRef();
  
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const bgHover = useColorModeValue('gray.50', 'gray.700');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      customName: '',
      description: '',
    },
  });

  // Expose submit function to parent
  useImperativeHandle(ref, () => ({
    getValues: () => ({
      customName: document.querySelector('input[name="customName"]')?.value || '',
      description: document.querySelector('textarea[name="description"]')?.value || '',
      public: isPublic,
      file: selectedFile,
    }),
    submit: () => {
      if (!selectedFile) {
        toast.error('لطفاً یک فایل انتخاب کنید');
        return;
      }
      
      const formData = {
        customName: document.querySelector('input[name="customName"]')?.value || '',
        description: document.querySelector('textarea[name="description"]')?.value || '',
        public: isPublic,
        file: selectedFile,
      };
      
      onSubmit(formData);
    }
  }));

  const handleFormSubmit = (data) => {
    if (!selectedFile) {
      toast.error('لطفاً یک فایل انتخاب کنید');
      return;
    }

    const fileData = {
      ...data,
      public: isPublic,
      file: selectedFile,
    };

    onSubmit(fileData);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return '🖼️';
      case 'mp4':
      case 'avi':
        return '🎥';
      case 'mp3':
      case 'wav':
        return '🎵';
      default:
        return '📎';
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* نام اختصاصی فایل */}
        <FormControl isInvalid={errors.customName}>
          <FormLabel>نام اختصاصی فایل</FormLabel>
          <Input
            {...register('customName', {
              maxLength: { value: 100, message: 'نام اختصاصی نمی‌تواند بیشتر از 100 کاراکتر باشد' },
            })}
            placeholder="نام اختصاصی برای فایل (اختیاری)"
          />
          <FormErrorMessage>
            {errors.customName && errors.customName.message}
          </FormErrorMessage>
        </FormControl>

        {/* توضیحات */}
        <FormControl isInvalid={errors.description}>
          <FormLabel>توضیحات</FormLabel>
          <Textarea
            {...register('description')}
            placeholder="توضیحات مربوط به فایل"
            rows={3}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </FormControl>

        {/* فایل عمومی */}
        <FormControl>
          <Checkbox
            isChecked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            colorScheme="teal"
          >
            فایل عمومی (قابل مشاهده برای همه)
          </Checkbox>
          <Text fontSize="sm" color="gray.500" mt={1}>
            اگر این گزینه را انتخاب کنید، فایل برای همه کاربران قابل مشاهده خواهد بود
          </Text>
        </FormControl>

        {/* آپلود فایل */}
        <FormControl>
          <FormLabel>انتخاب فایل *</FormLabel>
          <Box
            border="2px dashed"
            borderColor={selectedFile ? 'green.300' : borderColor}
            borderRadius="lg"
            p={6}
            textAlign="center"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{
              borderColor: 'teal.300',
              bg: bgHover,
            }}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {selectedFile ? (
              <VStack spacing={3}>
                <HStack spacing={2}>
                  <Text fontSize="2xl">{getFileIcon(selectedFile.name)}</Text>
                  <VStack spacing={1} align="start">
                    <Text fontWeight="bold">{selectedFile.name}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {formatFileSize(selectedFile.size)}
                    </Text>
                  </VStack>
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile();
                    }}
                  >
                    <Icon as={FaTimes} />
                  </Button>
                </HStack>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <Progress value={uploadProgress} size="sm" width="100%" />
                )}
              </VStack>
            ) : (
              <VStack spacing={3}>
                <Icon as={FaCloudUploadAlt} boxSize={10} color="gray.400" />
                <Text fontWeight="medium">فایل را اینجا رها کنید یا کلیک کنید</Text>
                <Text fontSize="sm" color="gray.500">
                  حداکثر حجم فایل: 10 مگابایت
                </Text>
              </VStack>
            )}
          </Box>
          <Input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.avi,.mp3,.wav"
            style={{ display: 'none' }}
          />
        </FormControl>

        {/* نمایش پیش‌نمایش فایل انتخاب شده */}
        {selectedFile && (
          <Box
            p={3}
            bg="green.50"
            borderRadius="md"
            border="1px solid"
            borderColor="green.200"
          >
            <HStack spacing={2}>
              <Icon as={FaFile} color="green.500" />
              <Text fontSize="sm" color="green.700">
                فایل انتخاب شده: {selectedFile.name}
              </Text>
            </HStack>
          </Box>
        )}
      </VStack>
    </form>
  );
});

export default FileUploadForm; 